/**
 * OmegaCorpusPage — Corpus Proof: the visible result surface for the 28M-row
 * read-only audit (item 11) and retroactive atomization (item 12).
 *
 * Founder-only. The audit runs over a DEDICATED read-only connection to atoms.db
 * (never the live engine mutex — the P0-safe rebuild), so it is safe to run on
 * demand. Atomization turns a pasted document into signed, tagged atoms via the
 * same ingest path the external-fetch loop uses, and shows the receipt.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface KindCount {
  kind: string;
  count: number;
}
interface AtomsAudit {
  total: number;
  by_kind: KindCount[];
  db_path: string;
  note: string;
}
interface AtomizeDocResult {
  title: string;
  domain?: string;
  atom_count?: number;
  converged_identity?: string;
  kind_counts?: Record<string, number>;
  error?: string;
}
interface AtomizeResult {
  docs: number;
  total_atoms: number;
  results: AtomizeDocResult[];
}

const PANEL: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
  marginBottom: 22,
};
const H2: React.CSSProperties = {
  margin: '0 0 6px',
  fontSize: '10pt',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#6b7280',
};
const BTN: React.CSSProperties = {
  background: '#4f46e5',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  padding: '8px 14px',
  fontWeight: 600,
  cursor: 'pointer',
};

function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaCorpusPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  // Item 11 — read-only 28M audit (manual trigger; it is a real query over the corpus).
  const audit = useMutation({
    mutationFn: () => apiFetch<AtomsAudit>('/omega/atoms/audit'),
  });

  // Item 12 — retroactive atomization of a pasted document.
  const [title, setTitle] = useState('Founder decision — Monitor product (12.6.26)');
  const [content, setContent] = useState('');
  const atomize = useMutation({
    mutationFn: () =>
      apiFetch<AtomizeResult>('/admin/atomize/docs', {
        method: 'POST',
        body: { docs: [{ title, content }] },
      }),
  });

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 16px 48px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Corpus Proof' }]} />
      <h1 style={{ margin: '8px 0 4px', fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        Corpus Proof
      </h1>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        The visible proof surface for the 28M-atom corpus audit and retroactive atomization.
      </p>

      {/* Item 11 — 28M read-only audit */}
      <section style={PANEL}>
        <h2 style={H2}>28M Corpus Audit (read-only — P0-safe)</h2>
        <p style={{ marginTop: 0, color: '#374151', fontSize: '9.5pt' }}>
          Breakdown of every atom by kind over the full corpus. Served from a dedicated
          read-only connection on a blocking thread — it never touches the live engine's
          atoms.db lock, so it cannot repeat the P0.
        </p>
        <button style={BTN} onClick={() => audit.mutate()} disabled={audit.isPending}>
          {audit.isPending ? 'Auditing 28M atoms…' : 'Run corpus audit'}
        </button>
        {audit.isError && (
          <p style={{ color: '#b91c1c' }}>Audit failed: {String((audit.error as Error)?.message ?? audit.error)}</p>
        )}
        {audit.data && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '14pt', fontWeight: 700 }}>{fmt(audit.data.total)} atoms total</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: '10pt' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: '4px 0' }}>Kind</th>
                  <th style={{ padding: '4px 0', textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.by_kind.map((k) => (
                  <tr key={k.kind} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 0' }}>{k.kind}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(k.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ color: '#9ca3af', fontSize: '8.5pt', marginTop: 8 }}>{audit.data.note}</p>
          </div>
        )}
      </section>

      {/* Item 12 — retroactive atomization */}
      <section style={PANEL}>
        <h2 style={H2}>Retroactive Atomization</h2>
        <p style={{ marginTop: 0, color: '#374151', fontSize: '9.5pt' }}>
          Paste a document (a decision, a spec, today's notes). It is decomposed into
          signed, tagged atoms via the same ingest path the learning loop uses, and the
          receipt shows exactly how many atoms it became.
        </p>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Document title"
          style={{ width: '100%', padding: 8, marginBottom: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Paste the document content to atomize…"
          rows={6}
          style={{ width: '100%', padding: 8, marginBottom: 8, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'inherit' }}
        />
        <button
          style={{ ...BTN, opacity: content.trim() ? 1 : 0.5 }}
          onClick={() => atomize.mutate()}
          disabled={atomize.isPending || !content.trim()}
        >
          {atomize.isPending ? 'Atomizing…' : 'Atomize document'}
        </button>
        {atomize.isError && (
          <p style={{ color: '#b91c1c' }}>Atomize failed: {String((atomize.error as Error)?.message ?? atomize.error)}</p>
        )}
        {atomize.data && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '14pt', fontWeight: 700 }}>
              {fmt(atomize.data.total_atoms)} signed atoms from {atomize.data.docs} document(s)
            </div>
            {atomize.data.results.map((r, i) => (
              <div key={i} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0', fontSize: '9.5pt' }}>
                <div style={{ fontWeight: 600 }}>{r.title}</div>
                {r.error ? (
                  <div style={{ color: '#b91c1c' }}>{r.error}</div>
                ) : (
                  <div style={{ color: '#374151' }}>
                    {r.atom_count} atoms · converged id{' '}
                    <code style={{ fontSize: '8.5pt' }}>{r.converged_identity?.slice(0, 16)}…</code>
                    {r.kind_counts && (
                      <span style={{ color: '#6b7280' }}>
                        {' '}· {Object.entries(r.kind_counts).map(([k, v]) => `${k}:${v}`).join(' ')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
